import { Navigate, type RouteObject } from 'react-router';
import { paths } from '@/config/paths';
import { createAnalyticsRoutes } from '@/features/analytics';
import { createAuthRoutes, GuestOnly, RequireAuth } from '@/features/auth';
import { createHelpRoutes } from '@/features/help';
import { createReportRoutes } from '@/features/reports';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { RouteErrorBoundary } from '@/pages/RouteErrorBoundary';
import type { RouteHandle } from '@/shared/routing';
import { Loading } from '@/shared/ui';
import { AppShell } from './layouts/AppShell';
import { AuthLayout } from './layouts/AuthLayout';
import { RootLayout } from './layouts/RootLayout';

/**
 * Route tree. Features own their route objects (with lazily loaded pages);
 * this file only composes them with guards and layouts.
 *
 * Routes are built by factories because the data router mutates route objects while resolving
 * `lazy` properties — every router instance (app, HMR, each test) needs its own copy.
 *
 *   RootLayout
 *   ├─ GuestOnly → AuthLayout → /signin, /signin/password, /signin/microsoft, /forgot-password
 *   └─ RequireAuth → AppShell → / → /overview, reports, lists, /profile, /data/**, /help/**, 404
 */
export const createAppRoutes = (): RouteObject[] => [
  {
    id: 'root',
    Component: RootLayout,
    ErrorBoundary: RouteErrorBoundary,
    HydrateFallback: () => <Loading />,
    children: [
      {
        Component: GuestOnly,
        children: [{ Component: AuthLayout, children: createAuthRoutes() }],
      },
      {
        Component: RequireAuth,
        children: [
          {
            Component: AppShell,
            children: [
              { index: true, element: <Navigate to={paths.overview} replace /> },
              ...createReportRoutes(),
              ...createAnalyticsRoutes(),
              ...createHelpRoutes(),
              {
                path: '*',
                Component: NotFoundPage,
                handle: { title: 'Not found' } satisfies RouteHandle,
              },
            ],
          },
        ],
      },
    ],
  },
];
